/**
 * OnThisDay — historical events that happened today, in the user's
 * language. One-tap expand for the full text + Wikipedia link, but the
 * 1-line headline is enough for most users.
 *
 * Source: Wikipedia REST API "On this day" feed
 *   https://api.wikimedia.org/feed/v1/wikipedia/{lang}/onthisday/events/{MM}/{DD}
 *
 * Free, no auth, ~ 200 KB response, returns up to ~50 events. We pick 3
 * (one per category — events / births / deaths) so users see a tasteful
 * mix without scrolling.
 *
 * Cached client-side via swr() — 12 h fresh (the data only changes once a
 * day at midnight UTC), 7 days stale-tolerant for offline users.
 */

import type { Widget, WidgetContext } from '../types';
import { swr } from '../../../services/swr';

interface WikiEvent {
  text: string;
  year: number;
  pages?: Array<{ titles?: { normalized?: string }; content_urls?: { desktop?: { page?: string } } }>;
}

interface OtdPayload {
  selected?: WikiEvent[];
  events?: WikiEvent[];
  births?: WikiEvent[];
  deaths?: WikiEvent[];
  fetchedAt: number;
}

const WIKI_LANGS = new Set(['en', 'fr', 'ar', 'es', 'de', 'it', 'pt', 'ru', 'tr', 'ja', 'ko', 'zh']);

async function fetchOtd(lang: string): Promise<OtdPayload | null> {
  const safeLang = WIKI_LANGS.has(lang) ? lang : 'en';
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const url = `https://api.wikimedia.org/feed/v1/wikipedia/${safeLang}/onthisday/all/${mm}/${dd}`;
  try {
    const resp = await fetch(url, { cache: 'no-store' });
    if (!resp.ok) return null;
    const data = await resp.json() as OtdPayload;
    return { ...data, fetchedAt: Date.now() };
  } catch {
    return null;
  }
}

function pickHighlights(p: OtdPayload): WikiEvent[] {
  const out: WikiEvent[] = [];
  // Prefer "selected" (Wikipedia-curated highlights); fallback to topics.
  const pool: WikiEvent[][] = [
    (p.selected || []).slice(0, 3),
    (p.events || []).slice(0, 3),
    (p.births || []).slice(0, 2),
    (p.deaths || []).slice(0, 2),
  ];
  // Round-robin to mix categories
  const maxLen = Math.max(...pool.map((a) => a.length));
  for (let i = 0; i < maxLen && out.length < 3; i++) {
    for (const arr of pool) {
      if (arr[i] && out.length < 3) out.push(arr[i]);
    }
  }
  return out;
}

export const OnThisDay: Widget = {
  id: 'on-this-day',
  label: {
    en: 'On this day',
    fr: 'Ce jour-là',
    ar: 'في مثل هذا اليوم',
    es: 'Tal día como hoy',
    de: 'An diesem Tag',
  },
  icon: '📜',
  defaultVisible: true,
  defaultOrder: 17,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const lang = (ctx.locale || 'en').split('-')[0];

    container.innerHTML = `
      <div style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">📜</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_otd_title') || 'On this day')}</span>
          <span style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">${escapeHtml(formatToday(ctx.locale))}</span>
        </div>
        <div data-otd-list>
          <div style="height:14px;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));border-radius:6px;margin-bottom:8px;" class="skeleton-pulse"></div>
          <div style="height:14px;width:80%;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));border-radius:6px;margin-bottom:8px;" class="skeleton-pulse"></div>
          <div style="height:14px;width:60%;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));border-radius:6px;" class="skeleton-pulse"></div>
        </div>
      </div>
    `;

    const list = container.querySelector('[data-otd-list]') as HTMLElement | null;

    const renderItems = (payload: OtdPayload): void => {
      if (!list) return;
      const picks = pickHighlights(payload);
      if (picks.length === 0) {
        list.innerHTML = `<div style="font-size:12px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">${escapeHtml(t('widget_otd_empty') || 'No notable events found for today.')}</div>`;
        return;
      }
      list.innerHTML = picks.map((ev, idx) => {
        const wikiUrl = ev.pages?.[0]?.content_urls?.desktop?.page || '';
        const id = `otd-${idx}`;
        return `
          <div style="padding:8px 0;border-bottom:${idx < picks.length - 1 ? '1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)))' : 'none'};">
            <button type="button" data-otd-toggle="${id}"
              style="display:flex;align-items:flex-start;gap:8px;width:100%;background:transparent;border:none;text-align:start;cursor:pointer;padding:0;color:inherit;font:inherit;">
              <span style="font-size:11px;font-weight:700;color:var(--ln-accent-ceremonial,#9C8348);font-variant-numeric:tabular-nums;flex-shrink:0;min-width:38px;">${ev.year}</span>
              <span style="font-size:13px;line-height:1.4;flex:1;color:var(--ln-text-primary,var(--text-primary,#1E293B));">${escapeHtml(truncate(ev.text, 120))}</span>
            </button>
            <div data-otd-detail="${id}" style="display:none;margin-top:6px;padding-inline-start:46px;">
              <div style="font-size:13px;line-height:1.5;color:var(--ln-text-primary,var(--text-primary,#1E293B));">${escapeHtml(ev.text)}</div>
              ${wikiUrl ? `<a href="${escapeAttr(wikiUrl)}" target="_blank" rel="noopener noreferrer"
                style="display:inline-block;margin-top:6px;font-size:11px;color:var(--ln-accent-ceremonial,#9C8348);text-decoration:underline;">${escapeHtml(t('widget_otd_read_more') || 'Read more →')}</a>` : ''}
            </div>
          </div>
        `;
      }).join('');

      list.querySelectorAll<HTMLButtonElement>('[data-otd-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.otdToggle || '';
          const detail = list.querySelector(`[data-otd-detail="${id}"]`) as HTMLElement | null;
          if (!detail) return;
          detail.style.display = detail.style.display === 'none' ? 'block' : 'none';
        });
      });
    };

    swr<OtdPayload>(
      `otd_${lang}_${new Date().toISOString().slice(0, 10)}`,
      () => fetchOtd(lang),
      {
        freshTtlMs: 12 * 60 * 60 * 1000,
        staleTtlMs: 7 * 24 * 60 * 60 * 1000,
        onUpdate: (data) => renderItems(data),
      },
    ).then((data) => {
      if (data) renderItems(data);
      else if (list) list.innerHTML = `<div style="font-size:12px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">${escapeHtml(t('widget_otd_offline') || 'Offline — try again later.')}</div>`;
    }).catch(() => { /* silent */ });
  },
};

function formatToday(locale: string): string {
  try {
    return new Date().toLocaleDateString(locale, { month: 'long', day: 'numeric' });
  } catch {
    return new Date().toLocaleDateString();
  }
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
